import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Download, TrendingDown, TrendingUp } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatAmount, formatPeriodShort } from "@/lib/format"
import { cn } from "@/lib/utils"
import { useToast } from "@/components/ui/use-toast"

import { Advertencias } from "@/routes/holding/Advertencias"
import { AvisoDeAlcance } from "@/routes/holding/AvisoDeAlcance"
import {
  exportarAnalisis,
  fetchAnalisis,
  type AnalisisDelGrupo,
  type Cambio,
  type Concentracion,
  type MesDelGrupo,
  type Runway,
  type SociedadComparada,
  type Tendencia,
} from "@/routes/holding/api"
import { useFiltrosGrupo } from "@/routes/holding/HoldingLayout"
import { Chart } from "@/components/charts/Chart"
import { t as traducir } from "@/lib/i18n"

/**
 * ANALISIS DEL GRUPO. La pelicula, no la foto.
 *
 * El Consolidado dice cuanto fue el grupo en agosto. Esta pantalla contesta lo
 * que un directorio pregunta despues, y que ningun estado contable contesta:
 * si va mejor o peor, que sociedad tira y cual frena, de cuantos clientes
 * depende el ingreso y cuantos meses de caja hay.
 *
 * ORDEN DE LECTURA, deliberado y el mismo que en Consolidado: primero lo que el
 * analisis NO pudo hacer, despues el alcance de quien mira, despues las cifras.
 * Una advertencia al pie se lee cuando ya creiste el numero.
 *
 * LO QUE ESTA PANTALLA EXISTE PARA DESMENTIR es el runway consolidado. Sumar
 * toda la caja y toda la quema da un numero correcto que no esta en ninguna
 * cuenta bancaria: el dinero de Panama no paga la planilla brasilena sin una
 * transferencia que alguien autoriza. Por eso el runway del grupo y el de la
 * sociedad mas corta van JUNTOS y con el mismo tamaño.
 */

/** Largos de serie que se miran de verdad. 24 es el tope del backend. */
const RANGOS = [6, 12, 24]

export function AnalisisPage() {
  const { periodo, moneda, libro } = useFiltrosGrupo()
  const [meses, setMeses] = React.useState(12)
  const [exportando, setExportando] = React.useState(false)
  const { toast } = useToast()

  const q = useQuery({
    queryKey: ["analisis-grupo", periodo, meses, moneda, libro],
    queryFn: () => fetchAnalisis(periodo, meses, moneda, libro),
  })
  const d = q.data

  async function descargar() {
    setExportando(true)
    try {
      await exportarAnalisis(periodo, meses, moneda, libro)
    } catch (error) {
      // Una descarga que falla en silencio es peor que un error: el usuario se
      // queda esperando un archivo que nunca llega y lo intenta otra vez.
      toast({
        variant: "destructive",
        title: "No se pudo exportar",
        description: error instanceof Error ? error.message : "Inténtalo de nuevo.",
      })
    } finally {
      setExportando(false)
    }
  }

  return (
    <div className="space-y-3">
      {d ? <Advertencias advertencias={d.advertencias} /> : null}
      {d ? <AvisoDeAlcance alcance={d.alcance} /> : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-0.5 rounded-md border bg-surface p-0.5">
          {RANGOS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMeses(m)}
              className={cn(
                "rounded-sm px-2 py-1 text-2xs font-medium transition-colors duration-100",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                meses === m
                  ? "bg-surface-sunken text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={meses === m}
            >
              {m} meses
            </button>
          ))}
        </div>
        <p className="text-2xs text-muted-foreground">
          {traducir("Cada mes de la serie es una consolidación completa: la serie más larga tarda más, y el número de cada punto es exactamente el que abre en Consolidado.")}</p>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => void descargar()}
          disabled={exportando || !d}
        >
          <Download />
          {exportando ? "Generando…" : "Exportar CSV"}
        </Button>
      </div>

      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && d?.tendencia == null}
        emptyMessage={traducir("No hay serie que analizar en este rango.")}
        emptyHint={traducir("Ningún mes del rango se pudo consolidar. Revisa que las sociedades tengan libro y que haya cotización para cada moneda.")}
        onRetry={() => void q.refetch()}
        columns={[20, 14, 14, 14, 14, 12]}
      >
        {d ? <Contenido d={d} /> : null}
      </DataState>
    </div>
  )
}

function Contenido({ d }: { d: AnalisisDelGrupo }) {
  return (
    <div className="space-y-3">
      {d.tendencia ? <Cabecera t={d.tendencia} moneda={d.moneda_presentacion} /> : null}

      <Evolucion meses={d.evolucion} moneda={d.moneda_presentacion} />

      <Comparativa
        filas={d.comparativa}
        moneda={d.moneda_presentacion}
        puente={d.suma_de_aportes}
        periodo={d.periodo}
      />

      <div className="grid gap-3 xl:grid-cols-2">
        {d.concentracion ? (
          <ConcentracionPanel
            titulo={traducir("Concentración por sociedad")}
            c={d.concentracion.por_sociedad}
            moneda={d.moneda_presentacion}
            columna="Sociedad"
            nota={traducir("De cuánto de las ventas del grupo responde cada sociedad. No es riesgo por sí solo —un grupo puede tener una sociedad grande a propósito— pero sí dice dónde duele un mal trimestre.")}
          />
        ) : null}
        {d.concentracion?.por_cliente ? (
          <ConcentracionPanel
            titulo={traducir("Concentración por cliente")}
            c={d.concentracion.por_cliente}
            moneda={d.moneda_presentacion}
            columna="Cliente"
            nota={d.concentracion.por_cliente.fuente}
          />
        ) : d.concentracion?.por_cliente_oculto ? (
          <WorkPanel title={traducir("Concentración por cliente")}>
            <p className="px-3 py-6 text-center text-xs leading-[1.2rem] text-muted-foreground">
              {d.concentracion.por_cliente_oculto}
            </p>
          </WorkPanel>
        ) : null}
      </div>

      {d.runway ? <RunwayPanel r={d.runway} moneda={d.moneda_presentacion} /> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ Cabecera */

/**
 * DOS COMPARACIONES, no una. El mes anterior capta el giro reciente; el mismo
 * mes del año pasado quita la estacionalidad, que en esta región es grande
 * —diciembre y julio no se parecen a nada— y hace que un «cae 30%» contra
 * noviembre no signifique nada por si solo.
 */
function Cabecera({ t, moneda }: { t: Tendencia; moneda: string }) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="panel px-4 py-3">
        <div className="eyebrow">{traducir("Ventas del grupo ·")}{" "}{formatPeriodShort(t.periodo)}</div>
        <div className="cifra mt-1 text-2xl">
          {formatAmount(t.ingresos)}{" "}
          <span className="code text-2xs font-normal text-muted-foreground">{moneda}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <Variacion etiqueta={traducir("vs. mes anterior")} cambio={t.vs_mes_anterior.ingresos} />
          <Variacion
            etiqueta={traducir("vs. año pasado")}
            cambio={t.vs_mismo_mes_del_anio_pasado.ingresos}
            nota={t.vs_mismo_mes_del_anio_pasado.nota}
          />
        </div>
      </div>

      <div className="panel px-4 py-3">
        <div className="eyebrow">{traducir("Resultado del grupo")}</div>
        <div
          className={cn(
            "cifra mt-1 text-2xl",
            t.utilidad_neta < 0 && "text-destructive",
          )}
        >
          {formatAmount(t.utilidad_neta)}{" "}
          <span className="code text-2xs font-normal text-muted-foreground">{moneda}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <Variacion etiqueta={traducir("vs. mes anterior")} cambio={t.vs_mes_anterior.utilidad_neta} />
          <Variacion
            etiqueta={traducir("vs. año pasado")}
            cambio={t.vs_mismo_mes_del_anio_pasado.utilidad_neta}
            nota={t.vs_mismo_mes_del_anio_pasado.nota}
          />
        </div>
      </div>

      <div className="panel px-4 py-3">
        <div className="eyebrow">{traducir("Margen neto consolidado")}</div>
        <div className="cifra mt-1 text-2xl">
          {t.margen_neto == null ? "—" : `${formatAmount(t.margen_neto)} %`}
        </div>
        <p className="mt-1 text-2xs leading-[0.95rem] text-muted-foreground">
          {traducir("Sobre el ingreso del grupo ya eliminado. No es el promedio de los márgenes de las sociedades: promediar márgenes da el margen de nadie.")}</p>
      </div>
    </div>
  )
}

/**
 * UN PORCENTAJE SOBRE UNA BASE NEGATIVA NO SE PINTA.
 *
 * Se veia «+100,00 % vs. mes anterior» encima de un resultado de 0,00: venia de
 * −16,46 y llegar a cero da +100% por aritmetica. Pero de −100 a −200 daria
 * «−100%», que suena a caida corriente cuando la perdida se DUPLICO. Con base
 * negativa se muestra el cambio absoluto, que es el unico que dice lo que
 * parece, y la moneda queda implicita como en el resto del panel.
 */
function Variacion({
  etiqueta,
  cambio,
  nota,
}: {
  etiqueta: string
  cambio: Cambio | null
  nota?: string | null
}) {
  if (cambio == null) {
    return (
      <span className="text-2xs text-muted-foreground/70" title={nota ?? undefined}>
        {etiqueta}{traducir(": sin comparación")}</span>
    )
  }
  const sube = (cambio.porcentaje ?? cambio.absoluto) >= 0
  const Icono = sube ? TrendingUp : TrendingDown
  const texto =
    cambio.porcentaje != null
      ? `${sube ? "+" : ""}${formatAmount(cambio.porcentaje)} %`
      : `${sube ? "+" : ""}${formatAmount(cambio.absoluto)}`
  return (
    <span
      className="inline-flex items-center gap-1 text-2xs"
      title={
        cambio.base_negativa
          ? "El mes de comparación fue negativo, así que un porcentaje diría lo contrario de lo que pasó: se muestra la diferencia en importe."
          : undefined
      }
    >
      <Icono className={cn("h-3 w-3", sube ? "text-success" : "text-destructive")} />
      <span className={cn("code", sube ? "text-success" : "text-destructive")}>{texto}</span>
      <span className="text-muted-foreground">{etiqueta}</span>
    </span>
  )
}

/* ----------------------------------------------------------------- Evolución */

/**
 * La serie, en gráfico y en tabla.
 *
 * UN MES SIN DATO NO SE PINTA COMO CERO. Se deja el hueco marcado: un punto en
 * cero se lee como un mes sin actividad, que es una afirmación —y falsa— cuando
 * lo que pasó es que faltaba una cotización. La regla no cambió; lo que cambió
 * es quién la aplica.
 *
 * ---------------------------------------------------------------------------
 * ERAN BARRAS DIBUJADAS A MANO, Y AHORA ES EL GRÁFICO DEL PRODUCTO
 *
 * El comentario que había aquí decía, con razón, que meter una librería de
 * 300 kB para doce barras es mal negocio. Sigue siendo verdad y por eso el
 * producto tiene su propio `Chart` en SVG, sin dependencias — así que usarlo no
 * añade nada y sí trae tres cosas que estas barras no tenían:
 *
 *  - **la lámina 19**: línea fina sobre el rayado del libro mayor y el último
 *    dato en rojo margen, que es lo que el brandbook fija para una TRAYECTORIA.
 *    Doce meses del grupo son una trayectoria, no una comparación de
 *    categorías, y una trayectoria se lee en línea;
 *  - **el hover con el valor**, que en las barras sólo estaba en el `title`;
 *  - **el eje**, que no había: las barras se leían unas contra otras y no
 *    contra ninguna escala.
 *
 * Y el hueco sigue siendo un hueco: `Chart` entiende `null` como «no hay dato»
 * —parte la línea y marca la columna— en vez de dibujar un cero.
 */
function Evolucion({ meses, moneda }: { meses: MesDelGrupo[]; moneda: string }) {
  const conDatos = meses.filter((m) => !m.sin_datos)
  const huecos = meses.length - conDatos.length

  return (
    <WorkPanel
      title={traducir("Evolución del grupo")}
      meta={`${conDatos.length} mes(es) consolidados · en ${moneda}`}
      footnote={
        huecos > 0
          ? `${huecos} mes(es) no se pudieron consolidar y no están en la serie. Una tendencia con huecos se lee como una caída: los huecos van marcados, no rellenados con cero.`
          : "Cada punto es una consolidación completa del mes, con su conversión, sus eliminaciones y su minoritario. Es el mismo número que abre en Consolidado."
      }
    >
      <div className="px-3 py-3">
        <Chart
          alto={220}
          etiquetas={meses.map((m) => formatPeriodShort(m.periodo))}
          series={[
            {
              nombre: "Ventas",
              valores: meses.map((m) => (m.sin_datos ? null : Number(m.ingresos ?? 0))),
            },
            {
              // El RESULTADO va en la misma escala y a propósito: las dos son
              // magnitudes de dinero del mismo grupo, y la distancia entre las
              // líneas ES el margen. Un segundo eje dejaría «demostrar» la
              // correlación que se quisiera moviendo una escala.
              nombre: "Utilidad neta",
              valores: meses.map((m) => (m.sin_datos ? null : Number(m.utilidad_neta ?? 0))),
            },
          ]}
        />
      </div>

      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead className="w-28">{traducir("Período")}</TableHead>
            <TableHead className="w-36 text-right">{traducir("Ventas")}</TableHead>
            <TableHead className="w-36 text-right">{traducir("Utilidad bruta")}</TableHead>
            <TableHead className="w-36 text-right">{traducir("EBITDA")}</TableHead>
            <TableHead className="w-36 text-right">{traducir("Utilidad neta")}</TableHead>
            <TableHead className="w-28 text-right">{traducir("Margen")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {meses.map((m) => (
            <TableRow key={m.periodo}>
              <TableCell className="code font-medium">{formatPeriodShort(m.periodo)}</TableCell>
              {m.sin_datos ? (
                <TableCell colSpan={5} className="text-xs text-destructive">
                  {traducir("No se pudo consolidar.")}{" "}{m.motivo}
                </TableCell>
              ) : (
                <>
                  <TableCell className="num-strong">{formatAmount(Number(m.ingresos ?? 0))}</TableCell>
                  <TableCell className="num">{formatAmount(Number(m.utilidad_bruta ?? 0))}</TableCell>
                  <TableCell className="num">{formatAmount(Number(m.ebitda ?? 0))}</TableCell>
                  <TableCell
                    className={cn("num", Number(m.utilidad_neta ?? 0) < 0 && "cifra-atencion")}
                  >
                    {formatAmount(Number(m.utilidad_neta ?? 0))}
                  </TableCell>
                  <TableCell className="num text-muted-foreground">
                    {m.margen_neto == null ? "—" : `${formatAmount(m.margen_neto)} %`}
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </WorkPanel>
  )
}

/* --------------------------------------------------------------- Comparativa */

/**
 * Sociedad contra sociedad, con la conversion ya aplicada.
 *
 * EL PIE ES EL PUENTE y no es decoracion: la columna de resultados NO suma el
 * consolidado, porque el grupo se factura a si mismo. Sin el puente, quien sume
 * y no le de busca un error que no existe — o, peor, lo da por bueno.
 */
function Comparativa({
  filas,
  moneda,
  puente,
  periodo,
}: {
  filas: SociedadComparada[]
  moneda: string
  puente: AnalisisDelGrupo["suma_de_aportes"]
  periodo: string
}) {
  return (
    <WorkPanel
      title={traducir("Sociedad contra sociedad")}
      meta={`${formatPeriodShort(periodo)} · en ${moneda}`}
      footnote={traducir("Cada sociedad con su propio libro convertido, ANTES de eliminar lo que se facturan entre ellas. Por eso la columna no suma el consolidado: la diferencia es exactamente lo eliminado, y está en el pie.")}
    >
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[15rem]">{traducir("Sociedad")}</TableHead>
            <TableHead className="w-14">{traducir("País")}</TableHead>
            <TableHead className="w-32 text-right">{traducir("Ventas")}</TableHead>
            <TableHead className="w-24 text-right">{traducir("Peso")}</TableHead>
            <TableHead className="w-32 text-right">{traducir("Utilidad neta")}</TableHead>
            <TableHead className="w-24 text-right">{traducir("Margen")}</TableHead>
            <TableHead className="w-28 text-right">{traducir("Aporte")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.map((f) => (
            <TableRow
              key={f.entity_id}
              className={cn(f.agregado && "text-muted-foreground")}
            >
              <TableCell
                className={cn("truncate", f.agregado ? "italic" : "font-medium")}
                title={f.razon_social ?? undefined}
              >
                {f.razon_social}
                {f.agregado ? ` (${f.sociedades})` : ""}
              </TableCell>
              <TableCell className="code">{f.pais ?? "—"}</TableCell>
              <TableCell className="num-strong">{formatAmount(f.ingresos)}</TableCell>
              <TableCell className="num text-muted-foreground">
                {f.peso_en_el_ingreso == null ? "—" : `${formatAmount(f.peso_en_el_ingreso)} %`}
              </TableCell>
              <TableCell className={cn("num", f.utilidad_neta < 0 && "cifra-atencion")}>
                {formatAmount(f.utilidad_neta)}
              </TableCell>
              <TableCell
                className={cn(
                  "num",
                  f.margen_neto != null && f.margen_neto < 0 && "text-destructive",
                )}
              >
                {f.margen_neto == null ? "—" : `${formatAmount(f.margen_neto)} %`}
              </TableCell>
              <TableCell className="num text-muted-foreground">
                {f.aporte_al_resultado == null ? "—" : `${formatAmount(f.aporte_al_resultado)} %`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        {puente ? (
          <tfoot>
            <TableRow className="bg-surface-sunken">
              <TableCell colSpan={4} className="rule-double text-xs">
                {traducir("Suma de las sociedades")}</TableCell>
              <TableCell className="num rule-double" colSpan={3}>
                {formatAmount(puente.utilidad_neta)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} className="text-xs text-muted-foreground">
                {traducir("Menos lo que se facturan entre ellas")}</TableCell>
              <TableCell className="num text-muted-foreground" colSpan={3}>
                {formatAmount(puente.eliminado_del_resultado)}
              </TableCell>
            </TableRow>
            <TableRow className="bg-surface-sunken">
              <TableCell colSpan={4} className="font-medium">
                {traducir("Resultado CONSOLIDADO")}</TableCell>
              <TableCell className="num-strong" colSpan={3}>
                {formatAmount(puente.utilidad_neta_consolidada)}
              </TableCell>
            </TableRow>
          </tfoot>
        ) : null}
      </Table>
    </WorkPanel>
  )
}

/* -------------------------------------------------------------- Concentración */

/**
 * El HHI y no solo el top-3.
 *
 * Tres clientes al 30% y uno al 80% con dos al 5% tienen un top-3 casi igual
 * —90% contra 95%— y riesgos muy distintos. El indice los separa por mas del
 * doble, y es el que mira un comite de riesgo.
 */
function ConcentracionPanel({
  titulo,
  c,
  moneda,
  columna,
  nota,
}: {
  titulo: string
  c: Concentracion
  moneda: string
  columna: string
  nota?: string | null
}) {
  /*
    SIN INGRESO NO SE PINTA SELLO. Con cero líneas el HHI da cero, cero cae en
    el tramo «baja» y el sello sale verde: «estás diversificado». La verdad es
    que no hay nada que medir, y las dos cosas no se parecen en nada. Lo
    descubrió el primer cálculo contra los datos reales, donde no había ninguna
    venta cargada — y en pantalla se veía tranquilizador.
  */
  const color =
    c.nivel === "alta" ? "destructive-soft" : c.nivel === "moderada" ? "warning-soft" : "success-soft"
  return (
    <WorkPanel
      title={titulo}
      meta={
        <>
          {c.nivel ? (
            <Badge variant={color} estado>
              {c.nivel}
            </Badge>
          ) : (
            <Badge variant="outline" estado>
              {traducir("sin ingreso")}</Badge>
          )}
          <span className="code text-2xs text-muted-foreground">
            {c.nivel
              ? `HHI ${formatAmount(c.hhi, { decimals: 0 })} · ${c.cuantos} en ${moneda}`
              : "no hay nada que medir"}
          </span>
        </>
      }
      footnote={nota}
    >
      {c.lineas.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          {traducir("No hubo ingreso en el período, así que no hay concentración que medir. No es lo mismo que estar diversificado.")}</p>
      ) : (
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead>{columna}</TableHead>
              <TableHead className="w-32 text-right">{traducir("Ventas")}</TableHead>
              <TableHead className="w-24 text-right">{traducir("Cuota")}</TableHead>
              <TableHead className="w-32">{traducir("Peso")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {c.lineas.slice(0, 12).map((l) => (
              <TableRow
                key={l.entity_id ?? l.party_id ?? l.razon_social}
                className={cn(l.agregado && "text-muted-foreground")}
              >
                <TableCell
                  className={cn("truncate", l.agregado && "italic")}
                  title={l.razon_social ?? undefined}
                >
                  {l.razon_social}
                  {l.agregado ? ` (${l.sociedades})` : ""}
                </TableCell>
                <TableCell className="num">{formatAmount(l.importe)}</TableCell>
                <TableCell className="num-strong">
                  {l.cuota == null ? "—" : `${formatAmount(l.cuota)} %`}
                </TableCell>
                <TableCell>
                  {/* La barra es el dato de un vistazo; la cifra, el dato exacto. */}
                  <div className="h-1.5 w-full rounded-full bg-surface-sunken">
                    <div
                      className="h-1.5 rounded-full bg-brand/70"
                      style={{ width: `${Math.min(100, Number(l.cuota ?? 0))}%` }}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </WorkPanel>
  )
}

/* -------------------------------------------------------------------- Runway */

/**
 * EL BLOQUE QUE ESTA PANTALLA EXISTE PARA QUE NO ENGAÑE.
 *
 * El runway consolidado suma toda la caja y toda la quema, y da un numero que
 * no esta en ninguna cuenta bancaria. Un grupo con catorce meses y una sociedad
 * con dos tiene un problema en dos meses, no en catorce. Por eso los dos van
 * juntos y con el mismo tamaño de cifra: el que manda para decidir es el corto.
 */
function RunwayPanel({ r, moneda }: { r: Runway; moneda: string }) {
  const corto = r.el_mas_corto
  return (
    <WorkPanel
      title={traducir("Runway")}
      meta={`quema promedio de ${r.meses_de_burn} meses · en ${moneda}`}
      footnote={
        <>
          {r.nota} <span className="block mt-0.5">{r.base}</span>
        </>
      }
    >
      <div className="grid gap-3 border-b p-3 md:grid-cols-2">
        <div className="rounded-md border bg-surface-sunken px-4 py-3">
          <div className="eyebrow">{traducir("El grupo, sumado")}</div>
          <div className="cifra mt-1 text-2xl">
            {r.consolidado.meses == null ? "sin quema" : `${formatAmount(r.consolidado.meses)} meses`}
          </div>
          <p className="mt-1 text-2xs leading-[0.95rem] text-muted-foreground">
            {formatAmount(r.consolidado.caja)} {moneda}{" "}{traducir("de caja ÷")}{" "}
            {formatAmount(r.consolidado.burn_mensual)}{" "}{traducir("al mes. Este número no está en ninguna cuenta bancaria.")}</p>
        </div>

        <div
          className={cn(
            "rounded-md border px-4 py-3",
            corto && corto.meses != null && Number(corto.meses) < 6
              ? "hatch-alert border-destructive/45"
              : "bg-surface-sunken",
          )}
        >
          <div className="eyebrow">{traducir("La sociedad más corta")}</div>
          {corto ? (
            <>
              <div
                className={cn(
                  "cifra mt-1 text-2xl",
                  corto.meses != null && Number(corto.meses) < 6 && "cifra-atencion",
                )}
              >
                {corto.meses == null ? "sin quema" : `${formatAmount(corto.meses)} meses`}
              </div>
              <p className="mt-1 text-2xs leading-[0.95rem] text-muted-foreground">
                {corto.razon_social}{traducir(". Es la que decide: mover dinero desde otra sociedad es una transferencia que alguien tiene que autorizar.")}</p>
            </>
          ) : (
            <p className="mt-1 text-xs leading-[1.2rem] text-muted-foreground">
              {r.el_mas_corto_oculto
                ? "La sociedad más corta del grupo no está a tu alcance. Decírtelo revelaría su caja dividida por su quema."
                : "Ninguna sociedad está quemando caja este trimestre."}
            </p>
          )}
        </div>
      </div>

      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[18rem]">{traducir("Sociedad")}</TableHead>
            <TableHead className="w-14">{traducir("País")}</TableHead>
            <TableHead className="w-36 text-right">{traducir("Caja")}</TableHead>
            <TableHead className="w-36 text-right">{traducir("Quema mensual")}</TableHead>
            <TableHead className="w-32 text-right">{traducir("Meses")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {r.por_sociedad.map((s) => (
            <TableRow key={s.entity_id}>
              <TableCell className="truncate font-medium" title={s.razon_social ?? undefined}>
                {s.razon_social}
                {s.sin_caja_asignada ? (
                  <span className="ml-2 text-2xs font-normal text-destructive">
                    {traducir("sin caja asignada")}</span>
                ) : null}
              </TableCell>
              <TableCell className="code">{s.pais ?? "—"}</TableCell>
              <TableCell className="num">{formatAmount(s.caja)}</TableCell>
              <TableCell className="num">{formatAmount(s.burn_mensual)}</TableCell>
              <TableCell
                className={cn(
                  "num-strong",
                  s.meses != null && Number(s.meses) < 6 && "cifra-atencion",
                )}
              >
                {s.meses == null ? "sin quema" : formatAmount(s.meses)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {r.sociedades_fuera_de_alcance ? (
        <p className="border-t px-3 py-2 text-2xs text-muted-foreground">
          {r.sociedades_fuera_de_alcance}{" "}{traducir("sociedad(es) no aparecen: su caja no está a tu alcance, y no se agregan en una línea porque de ahí se deduciría.")}</p>
      ) : null}
    </WorkPanel>
  )
}
